import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { HubConnectionBuilder } from '@microsoft/signalr';
import {
    Alert,
    AppBar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Fab,
    LinearProgress,
    Link,
    MenuItem,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Toolbar,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalculator,
    faCircleXmark,
    faDownload,
    faFloppyDisk,
    faLock,
    faPhoneVolume,
    faThumbsUp,
} from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { Api, CustomerDetails, OfferResponseType, ProjectInput, RespondToOfferRequest } from 'apiClient/Api';
import { CustomerDetailsForm } from './CustomerDetails';
import {
    CalculateResponse,
    CalculatorApiContext,
    RequirementGroup,
    RequirementItem,
} from './calculatorApi';
import {
    collectProject,
    defaultOptions,
    Instance,
    InstanceMap,
    newInstance,
    projectToInstances,
    withDefaultStandort,
} from './instanceModel';
import { RequirementInputTree } from './RequirementInputTree';
import { Warenkorb } from './Warenkorb';

const SESSION_KEY = 'ok-public-session';

interface PublicOffer {
    id?: string;
    fileName?: string;
    offerNumber?: string;
    totalSum?: number;
    responseType?: OfferResponseType | string | null;
    responseAt?: string | null;
    responseByName?: string | null;
    responseMessage?: string | null;
    plannedMonth?: number | null;
    plannedYear?: number | null;
    downloadCount?: number | null;
    lastDownloadedAt?: string | null;
}
interface PublicConfigState {
    mode?: 'View' | 'Edit';
    projectName?: string;
    project?: ProjectInput;
    customerNotes?: string | null;
    customerDetails?: CustomerDetails | null;
    releasedOffers?: PublicOffer[];
}

const money = (v?: number) =>
    v == null ? '' : Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const MONTHS = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const RESPONSE_LABEL: Record<string, string> = {
    Approved: 'Für uns in Ordnung – intern weitergegeben',
    NotSatisfied: 'Nicht zufrieden',
    CallbackRequested: 'Rückruf erwünscht',
};

// Kurzer Hinweis beim Hover über die Auswahl.
const RESPONSE_HINT: Record<string, string> = {
    Approved: 'Intern freigeben und selbst weiter bearbeiten',
    NotSatisfied: 'Angebot passt noch nicht – Rückmeldung geben',
    CallbackRequested: 'Wir rufen Sie zurück',
};

// Ausführliche Erklärung, sobald eine Option gewählt wurde (Info-Text darunter).
const RESPONSE_INFO: Record<string, string> = {
    Approved:
        'Sie geben das Angebot damit intern zur weiteren Bearbeitung frei und werden selbst tätig (z. B. Bestellung/Freigabe im Haus). Bitte geben Sie an, wann Sie die Umsetzung grob planen. Wir werden über Ihre Rückmeldung informiert.',
    NotSatisfied:
        'Etwas passt noch nicht. Bitte beschreiben Sie kurz, was – wir überarbeiten das Angebot und melden uns bei Ihnen.',
    CallbackRequested:
        'Sie haben noch Fragen oder möchten etwas besprechen – wir rufen Sie zurück. Optional können Sie uns eine Nachricht hinterlassen.',
};

const indexTree = (group: RequirementGroup, into: Map<string, RequirementItem>) => {
    for (const i of group.items) into.set(i.id, i);
    for (const s of group.groups) indexTree(s, into);
};
const emptyInstances = (index: Map<string, RequirementItem>): InstanceMap => {
    const map: InstanceMap = {};
    index.forEach((item, id) => {
        map[id] = [newInstance(0, defaultOptions(item.optionGroups))];
    });
    return map;
};

/** Freigegebenes Angebot: ansehen (PDF) + mehrstufige Rückmeldung (in Ordnung / nicht zufrieden / Rückruf). */
const OfferCard: React.FC<{
    offer: PublicOffer;
    onDownload: (id: string, name?: string) => void;
    onRespond: (id: string, req: RespondToOfferRequest) => Promise<void>;
}> = ({ offer, onDownload, onRespond }) => {
    const [name, setName] = useState(offer.responseByName || '');
    const [choice, setChoice] = useState<OfferResponseType | null>(null);
    const [message, setMessage] = useState(offer.responseMessage || '');
    const [plannedMonth, setPlannedMonth] = useState<number | ''>(offer.plannedMonth || '');
    const [plannedYear, setPlannedYear] = useState<number | ''>(offer.plannedYear || '');
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(false);

    const hasResponse = !!offer.responseAt;
    const showForm = !hasResponse || editing;
    const rt = String(offer.responseType || '');
    const severity = rt === 'Approved' ? 'success' : rt === 'NotSatisfied' ? 'warning' : 'info';
    // Bei „Für uns in Ordnung" ist die grobe geplante Umsetzung (Monat + Jahr) Pflicht.
    const plannedMissing = choice === OfferResponseType.Approved && (!plannedMonth || !plannedYear);

    const submit = async () => {
        if (!offer.id || !choice) return;
        setBusy(true);
        try {
            await onRespond(offer.id, {
                type: choice,
                name: name || null,
                message: choice === OfferResponseType.Approved ? null : message || null,
                plannedMonth: choice === OfferResponseType.Approved && plannedMonth ? plannedMonth : null,
                plannedYear: choice === OfferResponseType.Approved && plannedYear ? plannedYear : null,
            });
            setEditing(false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
                <Typography sx={{ fontWeight: 600 }}>
                    Angebot {offer.offerNumber || ''} {offer.totalSum != null ? `· ${money(offer.totalSum)} netto` : ''}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                    startIcon={<FontAwesomeIcon icon={faDownload} />}
                    onClick={() => offer.id && onDownload(offer.id, offer.fileName)}
                >
                    Angebot herunterladen (PDF)
                </Button>
            </Stack>

            {hasResponse && (
                <Alert severity={severity as 'success' | 'warning' | 'info'} sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {RESPONSE_LABEL[rt] || 'Rückmeldung erfasst'}
                        {offer.responseAt ? ` · ${new Date(offer.responseAt).toLocaleDateString('de-DE')}` : ''}
                        {offer.responseByName ? ` · ${offer.responseByName}` : ''}
                    </Typography>
                    {offer.responseMessage && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {offer.responseMessage}
                        </Typography>
                    )}
                    {offer.plannedMonth && offer.plannedYear && (
                        <Typography variant="body2">
                            Ca. geplante Umsetzung: {MONTHS[offer.plannedMonth - 1]} {offer.plannedYear}
                        </Typography>
                    )}
                    {!editing && (
                        <Button size="small" sx={{ mt: 0.5 }} onClick={() => setEditing(true)}>
                            Rückmeldung ändern
                        </Button>
                    )}
                </Alert>
            )}

            {showForm && (
                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        Ihre Rückmeldung an uns:
                    </Typography>
                    <ToggleButtonGroup
                        value={choice}
                        exclusive
                        size="small"
                        onChange={(_, v) => v && setChoice(v)}
                        sx={{ flexWrap: 'wrap' }}
                    >
                        <ToggleButton value={OfferResponseType.Approved} color="success" title={RESPONSE_HINT.Approved}>
                            <FontAwesomeIcon icon={faThumbsUp} style={{ marginRight: 8 }} />
                            Für uns in Ordnung
                        </ToggleButton>
                        <ToggleButton
                            value={OfferResponseType.NotSatisfied}
                            color="warning"
                            title={RESPONSE_HINT.NotSatisfied}
                        >
                            <FontAwesomeIcon icon={faCircleXmark} style={{ marginRight: 8 }} />
                            Nicht zufrieden
                        </ToggleButton>
                        <ToggleButton
                            value={OfferResponseType.CallbackRequested}
                            color="info"
                            title={RESPONSE_HINT.CallbackRequested}
                        >
                            <FontAwesomeIcon icon={faPhoneVolume} style={{ marginRight: 8 }} />
                            Rückruf erwünscht
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {choice && (
                        <Alert severity="info" sx={{ py: 0.5 }}>
                            {RESPONSE_INFO[String(choice)]}
                        </Alert>
                    )}

                    {choice === OfferResponseType.Approved && (
                        <Stack spacing={1}>
                            <Typography variant="caption" color="text.secondary">
                                Ca. geplante Umsetzung (erforderlich)
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    select
                                    required
                                    size="small"
                                    label="Monat"
                                    value={plannedMonth}
                                    error={plannedMissing && !plannedMonth}
                                    onChange={(e) => setPlannedMonth(e.target.value ? Number(e.target.value) : '')}
                                    sx={{ minWidth: 140 }}
                                >
                                    <MenuItem value="">–</MenuItem>
                                    {MONTHS.map((m, i) => (
                                        <MenuItem key={m} value={i + 1}>
                                            {m}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    size="small"
                                    type="number"
                                    required
                                    label="Jahr"
                                    value={plannedYear}
                                    error={plannedMissing && !plannedYear}
                                    onChange={(e) => setPlannedYear(e.target.value ? Number(e.target.value) : '')}
                                    sx={{ width: 120 }}
                                />
                            </Stack>
                        </Stack>
                    )}

                    {(choice === OfferResponseType.NotSatisfied || choice === OfferResponseType.CallbackRequested) && (
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            size="small"
                            label={
                                choice === OfferResponseType.NotSatisfied
                                    ? 'Was passt nicht? (optional)'
                                    : 'Ihre Nachricht (optional)'
                            }
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                        />
                    )}

                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <TextField size="small" label="Ihr Name" value={name} onChange={(e) => setName(e.target.value)} />
                        <Button variant="contained" disabled={!choice || busy || plannedMissing} onClick={submit}>
                            Absenden
                        </Button>
                        {hasResponse && editing && (
                            <Button color="inherit" onClick={() => setEditing(false)}>
                                Abbrechen
                            </Button>
                        )}
                    </Stack>
                </Stack>
            )}
        </Paper>
    );
};

/**
 * Rahmen des öffentlichen Konfigurators: gebrandeter Header (Reichert-Logo), scrollbarer Inhalt
 * und schlichter Footer mit Impressum/Datenschutz. Nutzbar in beiden Phasen (Login/Konfigurator).
 */
const PublicShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'grey.100' }}>
        <AppBar
            position="static"
            elevation={0}
            sx={{ bgcolor: 'background.paper', color: 'text.primary', borderBottom: 1, borderColor: 'divider' }}
        >
            <Toolbar>
                <Box component="img" src="/logo-2048.png" alt="Reichert GmbH" sx={{ height: 30 }} />
                <Divider orientation="vertical" flexItem sx={{ mx: 2, my: 1.5 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Konfigurator
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                    Reichert GmbH
                </Typography>
            </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {children}
            <Box sx={{ py: 3, textAlign: 'center' }}>
                <Box
                    component="img"
                    src="/logo-2048.png"
                    alt="Reichert GmbH"
                    sx={{ height: 20, display: 'block', mx: 'auto', mb: 1, opacity: 0.7 }}
                />
                <Typography variant="caption" color="text.secondary">
                    <Link href="https://reichert-gmbh.com/impressum/" target="_blank" color="inherit">
                        Impressum
                    </Link>
                    {' · '}
                    <Link href="https://www.reichert-gmbh.com/datenschutz/" target="_blank" color="inherit">
                        Datenschutz
                    </Link>
                </Typography>
            </Box>
        </Box>
    </Box>
);

export const PublicConfigurator: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const { enqueueSnackbar } = useSnackbar();

    // Eigene API-Instanz mit Session-Token (nicht das App-JWT).
    const sessionApi = useMemo(() => {
        // ServerConfig ist ein globales `const` aus public/config.js – NICHT an window gebunden.
        // @ts-ignore
        const baseURL = ServerConfig.SERVER_URL as string;
        const a = new Api<string>({
            baseURL,
            securityWorker: (t) => (t ? { headers: { Authorization: 'Bearer ' + t } } : {}),
        });
        const saved = sessionStorage.getItem(SESSION_KEY);
        if (saved) a.setSecurityData(saved);
        return a;
    }, []);

    const [phase, setPhase] = useState<'login' | 'ready'>('login');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | undefined>(undefined);
    const [busy, setBusy] = useState(false);

    const [state, setState] = useState<PublicConfigState | null>(null);
    const [tree, setTree] = useState<RequirementGroup | null>(null);
    const [index, setIndex] = useState<Map<string, RequirementItem>>(new Map());
    const [instances, setInstances] = useState<InstanceMap>({});
    const [activeStandort, setActiveStandort] = useState('A');
    const [notes, setNotes] = useState('');
    const [details, setDetails] = useState<CustomerDetails>({});
    const [response, setResponse] = useState<CalculateResponse | null>(null);
    const [calcBusy, setCalcBusy] = useState(false);
    // Merker: Kalkulation nach dem Öffnen/Live-Refresh einmal automatisch anzeigen.
    const autoCalcRef = useRef(false);
    // Live-Verbindungsstatus (SignalR).
    const [live, setLive] = useState(false);
    // Ungespeicherte Änderungen (steuert den pulsierenden Speichern-Button).
    const [dirty, setDirty] = useState(false);

    // Browser-Tab-Titel: „Konfigurator – <Projektname>".
    useEffect(() => {
        document.title = state?.projectName ? `Konfigurator – ${state.projectName}` : 'Konfigurator';
    }, [state?.projectName]);

    const standorte = useMemo(() => withDefaultStandort(state?.project?.standorte), [state]);
    const isEdit = state?.mode === 'Edit';

    const applyState = useCallback(
        async (s: PublicConfigState) => {
            setState(s);
            setNotes(s.customerNotes || '');
            setDetails(s.customerDetails || {});
            const treeRes = await sessionApi.calculator.calculatorGetRequirements();
            const t = treeRes.data as unknown as RequirementGroup;
            const idx = new Map<string, RequirementItem>();
            indexTree(t, idx);
            setTree(t);
            setIndex(idx);
            setInstances({ ...emptyInstances(idx), ...projectToInstances(s.project ?? { projektname: '', instanzen: [] }) });
            autoCalcRef.current = true;
            setDirty(false);
        },
        [sessionApi],
    );

    const is401 = (e: unknown) => (e as { response?: { status?: number } })?.response?.status === 401;

    // Session abgelaufen/ungültig → zurück zum Passwort-Schritt.
    const expireSession = useCallback(() => {
        sessionStorage.removeItem(SESSION_KEY);
        sessionApi.setSecurityData('');
        setResponse(null);
        setPassword('');
        setPhase('login');
        setError('Ihre Sitzung ist abgelaufen. Bitte geben Sie das Passwort erneut ein.');
    }, [sessionApi]);

    // Auto-Resume: gültiges Session-Token aus sessionStorage → ohne erneutes Passwort öffnen.
    useEffect(() => {
        const saved = sessionStorage.getItem(SESSION_KEY);
        if (!saved) return;
        let active = true;
        sessionApi.public
            .publicConfiguratorGetState()
            .then(async (r) => {
                if (!active) return;
                await applyState(r.data as unknown as PublicConfigState);
                setPhase('ready');
            })
            .catch(() => sessionStorage.removeItem(SESSION_KEY));
        return () => {
            active = false;
        };
    }, [sessionApi, applyState]);

    const open = async () => {
        if (!token || !password.trim()) return;
        setBusy(true);
        setError(undefined);
        try {
            const r = await sessionApi.public.publicConfiguratorOpen(token, { password });
            const data = r.data as unknown as { sessionToken: string; state: PublicConfigState };
            sessionApi.setSecurityData(data.sessionToken);
            sessionStorage.setItem(SESSION_KEY, data.sessionToken);
            await applyState(data.state);
            setPhase('ready');
        } catch (e) {
            const status = (e as { response?: { status?: number } })?.response?.status;
            setError(
                status === 401
                    ? 'Falsches Passwort.'
                    : status === 404
                        ? 'Link ungültig.'
                        : status === 410
                            ? 'Dieser Link wurde widerrufen oder ist abgelaufen.'
                            : status === 429
                                ? 'Zu viele Versuche – bitte später erneut.'
                                : 'Öffnen fehlgeschlagen.',
            );
        } finally {
            setBusy(false);
        }
    };

    // ---- Instanz-Mutatoren (Edit-Modus) ----
    const addInstance = useCallback(
        (reqId: string, area: string) => {
            const inst = newInstance(0, defaultOptions(index.get(reqId)?.optionGroups));
            inst.area = area;
            setInstances((prev) => ({ ...prev, [reqId]: [...(prev[reqId] || []), inst] }));
            setDirty(true);
        },
        [index],
    );
    const ensureInstances = useCallback(
        (reqIds: string[], area: string) => {
            setInstances((prev) => {
                let changed = false;
                const next = { ...prev };
                for (const reqId of reqIds) {
                    const list = next[reqId] || [];
                    if (!list.some((i) => (i.area || 'A') === area)) {
                        const inst = newInstance(0, defaultOptions(index.get(reqId)?.optionGroups));
                        inst.area = area;
                        next[reqId] = [...list, inst];
                        changed = true;
                    }
                }
                return changed ? next : prev;
            });
        },
        [index],
    );
    const removeInstance = useCallback((reqId: string, iid: number) => {
        setInstances((prev) => {
            const target = (prev[reqId] || []).find((i) => i.iid === iid);
            const area = target?.area || 'A';
            const next = (prev[reqId] || []).filter((i) => i.iid !== iid);
            if (!next.some((i) => (i.area || 'A') === area)) {
                const empty = newInstance();
                empty.area = area;
                next.push(empty);
            }
            return { ...prev, [reqId]: next };
        });
        setDirty(true);
    }, []);
    const updateInstance = useCallback((reqId: string, iid: number, patch: Partial<Instance>) => {
        setInstances((prev) => ({ ...prev, [reqId]: (prev[reqId] || []).map((i) => (i.iid === iid ? { ...i, ...patch } : i)) }));
        setDirty(true);
    }, []);
    const updateOpt = useCallback(
        (reqId: string, iid: number, fn: (prev: Record<string, number>) => Record<string, number>) => {
            setInstances((prev) => ({ ...prev, [reqId]: (prev[reqId] || []).map((i) => (i.iid === iid ? { ...i, opt: fn(i.opt) } : i)) }));
            setDirty(true);
        },
        [],
    );

    const calculate = useCallback(async () => {
        setCalcBusy(true);
        try {
            const project = collectProject(state?.projectName || '', instances, index, standorte);
            const r = await sessionApi.calculator.calculatorCalculateProject({ project });
            setResponse(r.data as unknown as CalculateResponse);
        } catch (e) {
            if (is401(e)) expireSession();
            else enqueueSnackbar('Kalkulation fehlgeschlagen.', { variant: 'error' });
        } finally {
            setCalcBusy(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionApi, state, instances, index, standorte, enqueueSnackbar]);

    const save = async () => {
        try {
            const project = isEdit ? collectProject(state?.projectName || '', instances, index, standorte) : undefined;
            const r = await sessionApi.public.publicConfiguratorSave({
                project,
                customerNotes: notes,
                customerDetails: details,
            });
            setState((r.data as unknown as PublicConfigState) ?? state);
            setDirty(false);
            enqueueSnackbar('Gespeichert.', { variant: 'success' });
        } catch (e) {
            if (is401(e)) expireSession();
            else enqueueSnackbar('Speichern fehlgeschlagen.', { variant: 'error' });
        }
    };

    // „Aktualisieren" für den Kunden: erst speichern, dann neu berechnen.
    const saveAndCalculate = async () => {
        await save();
        await calculate();
    };

    const downloadOffer = async (offerId: string, name?: string) => {
        try {
            const r = await sessionApi.public.publicConfiguratorDownloadOffer(offerId, { format: 'blob' });
            const url = URL.createObjectURL(r.data as unknown as Blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = name || 'angebot.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            if (is401(e)) expireSession();
            else enqueueSnackbar('Download fehlgeschlagen.', { variant: 'error' });
        }
    };

    const respondOffer = async (offerId: string, req: RespondToOfferRequest) => {
        try {
            await sessionApi.public.publicConfiguratorRespondToOffer(offerId, req);
            const msg =
                req.type === OfferResponseType.Approved
                    ? 'Danke! Das Angebot wird intern weiterverfolgt.'
                    : req.type === OfferResponseType.NotSatisfied
                        ? 'Danke für Ihre Rückmeldung – wir kümmern uns darum.'
                        : 'Danke! Wir melden uns für einen Rückruf.';
            enqueueSnackbar(msg, { variant: 'success' });
            const st = await sessionApi.public.publicConfiguratorGetState();
            setState((st.data as unknown as PublicConfigState) ?? state);
        } catch (e) {
            if (is401(e)) expireSession();
            else enqueueSnackbar('Senden fehlgeschlagen.', { variant: 'error' });
        }
    };

    // Live-Aktualisierung: aktuellen Stand (preisfrei) neu holen und Konfiguration/Kalkulation
    // nachziehen – in beiden Modi. Notizen/Anschrift (aktuelle Eingabe) bleiben unangetastet.
    const refreshFromServer = useCallback(async () => {
        try {
            const r = await sessionApi.public.publicConfiguratorGetState();
            const s = r.data as unknown as PublicConfigState;
            setState(s);
            setInstances({
                ...emptyInstances(index),
                ...projectToInstances(s.project ?? { projektname: '', instanzen: [] }),
            });
            autoCalcRef.current = true;
            setDirty(false);
        } catch (e) {
            if (is401(e)) expireSession();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionApi, index, expireSession]);

    const refreshRef = useRef(refreshFromServer);
    useEffect(() => {
        refreshRef.current = refreshFromServer;
    }, [refreshFromServer]);

    // SignalR-Hub: auf Konfigurationsänderungen hören (Office klickt zusammen → Kunde sieht es live).
    useEffect(() => {
        if (phase !== 'ready') return;
        if (!sessionStorage.getItem(SESSION_KEY)) return;
        // ServerConfig ist ein globales `const` aus public/config.js – NICHT an window gebunden.
        // @ts-ignore
        const baseURL = (ServerConfig.SERVER_URL as string) || '';
        let stopping = false;
        const hubUrl = `${baseURL}/hubs/configurator`;
        const conn = new HubConnectionBuilder()
            // Token je Verbindungsversuch frisch lesen; bei Ablauf ist es weg → Reconnect scheitert (401).
            .withUrl(hubUrl, {
                accessTokenFactory: () => sessionStorage.getItem(SESSION_KEY) || '',
            })
            .withAutomaticReconnect()
            .build();
        conn.on('ConfiguratorConfigChanged', (payload) => {
            console.info('Konfigurator-Hub: Live-Signal empfangen', payload);
            refreshRef.current();
        });
        conn.onreconnecting(() => setLive(false));
        conn.onreconnected(() => {
            setLive(true);
            refreshRef.current();
        });
        // Reconnect erschöpft/fehlgeschlagen (z. B. Session abgelaufen) → zurück zur Passwort-Ansicht,
        // statt endlos zu reconnecten. Bei bewusstem Stop (Unmount) nichts tun.
        conn.onclose((err) => {
            setLive(false);
            if (err) console.warn('Konfigurator-Hub: Verbindung geschlossen', err);
            if (!stopping && err) expireSession();
        });
        conn.start()
            .then(() => {
                setLive(true);
                console.info('Konfigurator-Hub: verbunden mit', hubUrl);
            })
            .catch((e) => {
                setLive(false);
                console.warn('Konfigurator-Hub: Verbindung fehlgeschlagen für', hubUrl, e);
            });
        return () => {
            stopping = true;
            setLive(false);
            conn.stop().catch(() => undefined);
        };
    }, [phase, expireSession]);

    // Kalkulation nach dem Öffnen automatisch anzeigen (einmalig je Öffnen/Live-Refresh).
    useEffect(() => {
        if (autoCalcRef.current && tree && Object.keys(instances).length > 0) {
            autoCalcRef.current = false;
            calculate();
        }
    }, [instances, tree, calculate]);

    // --- Login-Schritt ---
    if (phase === 'login') {
        return (
            <PublicShell>
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Paper sx={{ p: 4 }}>
                    <Stack spacing={2} sx={{ alignItems: 'center' }}>
                        <FontAwesomeIcon icon={faLock} size="2x" />
                        <Typography variant="h5">Ihr Konfigurator</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                            Bitte geben Sie das Passwort aus Ihrer E-Mail ein.
                        </Typography>
                        {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
                        <TextField
                            fullWidth
                            type="password"
                            label="Passwort"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && open()}
                            autoFocus
                        />
                        <Button variant="contained" fullWidth onClick={open} disabled={busy || !password.trim()}>
                            {busy ? <CircularProgress size={22} /> : 'Öffnen'}
                        </Button>
                    </Stack>
                </Paper>
            </Container>
            </PublicShell>
        );
    }

    // --- Konfigurator ---
    const pricesVisible = response?.pricesVisible !== false; // öffentlich immer false → blurry
    return (
        <CalculatorApiContext.Provider value={sessionApi as unknown as Api<unknown>}>
            <PublicShell>
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }} useFlexGap>
                    <Typography variant="h4">{state?.projectName || 'Konfigurator'}</Typography>
                    <Chip
                        size="small"
                        color={live ? 'success' : 'default'}
                        variant={live ? 'filled' : 'outlined'}
                        label={live ? 'Live aktiv' : 'Nicht verbunden'}
                    />
                </Stack>

                {isEdit && tree && (
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Ihre Anlage</Typography>
                            <Button
                                variant="contained"
                                startIcon={<FontAwesomeIcon icon={faCalculator} />}
                                onClick={saveAndCalculate}
                                disabled={calcBusy}
                            >
                                Speichern &amp; aktualisieren
                            </Button>
                        </Stack>
                        <RequirementInputTree
                            tree={tree}
                            instances={instances}
                            standorte={standorte}
                            activeStandort={activeStandort}
                            onSelectStandort={setActiveStandort}
                            reqOrder="xml"
                            addInstance={addInstance}
                            removeInstance={removeInstance}
                            updateInstance={updateInstance}
                            updateOpt={updateOpt}
                            ensureInstances={ensureInstances}
                        />
                    </Paper>
                )}

                {/* Kundendaten / Anschrift */}
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Ihre Daten &amp; Anschrift
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Diese Angaben helfen uns bei Angebot und Lieferung. Alle Felder sind optional.
                    </Typography>
                    <CustomerDetailsForm
                        value={details}
                        onChange={(d) => {
                            setDetails(d);
                            setDirty(true);
                        }}
                    />
                    <Box sx={{ mt: 2 }}>
                        <Button variant="contained" onClick={save}>
                            Speichern
                        </Button>
                    </Box>
                </Paper>

                {/* Notizen */}
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Ihre Notizen
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder="Wünsche / Anmerkungen …"
                        value={notes}
                        onChange={(e) => {
                            setNotes(e.target.value);
                            setDirty(true);
                        }}
                    />
                    <Box sx={{ mt: 2 }}>
                        <Button variant="contained" onClick={save}>
                            Speichern
                        </Button>
                    </Box>
                </Paper>

                {/* Kalkulation – direkt sichtbar */}
                {calcBusy && <LinearProgress sx={{ mb: 2 }} />}
                {response && (
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6">Ihre Kalkulation</Typography>
                            <Button
                                size="small"
                                startIcon={<FontAwesomeIcon icon={faCalculator} />}
                                onClick={isEdit ? saveAndCalculate : calculate}
                                disabled={calcBusy}
                            >
                                {isEdit ? 'Speichern & aktualisieren' : 'Aktualisieren'}
                            </Button>
                        </Stack>
                        <Warenkorb
                            positions={response.result.positions}
                            summary={response.result.summary}
                            standorte={standorte}
                            pricesVisible={pricesVisible}
                        />
                    </Paper>
                )}

                {/* Angebot */}
                {(state?.releasedOffers?.length ?? 0) > 0 && (
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Ihr Angebot
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                        {state!.releasedOffers!.map((o) => (
                            <OfferCard key={o.id} offer={o} onDownload={downloadOffer} onRespond={respondOffer} />
                        ))}
                    </Paper>
                )}
            </Container>

            {/* Schwebender Speichern-Button – pulsiert bei ungespeicherten Änderungen. */}
            {dirty && (
                <Fab
                    variant="extended"
                    color="primary"
                    onClick={save}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        zIndex: 1300,
                        '@keyframes okPulse': {
                            '0%': { transform: 'scale(1)', boxShadow: 3 },
                            '50%': { transform: 'scale(1.08)', boxShadow: 8 },
                            '100%': { transform: 'scale(1)', boxShadow: 3 },
                        },
                        animation: 'okPulse 1.2s ease-in-out infinite',
                    }}
                >
                    <FontAwesomeIcon icon={faFloppyDisk} style={{ marginRight: 8 }} />
                    Speichern
                </Fab>
            )}
            </PublicShell>
        </CalculatorApiContext.Provider>
    );
};

export default PublicConfigurator;
